import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Button, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useInventory } from '../../../../contexts/InventoryContext';
import ProjectOverview from './ProjectOverview';
import ProjectDetailsHeader from './ProjectDetailsHeader';
import ConfigurationList from './ConfigurationList';
import AddNewConfiguration from '../AddNewConfiguration';
import CSVImportConfigurations from '../../components/CSVImportConfigurations';
import MediaAndDocuments from '../../../../components/inventory/MediaAndDocuments';

const ProjectDetail = ({ defaultTab }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getProjectDetail, getConfigsByProjectId, createConfiguration, updateProject, getMedia, inventoryOptions } = useInventory();
  const [tab, setTab] = useState(defaultTab === 'configurations' ? 'configuration' : (defaultTab || 'overview'));
  const [configDrawerOpen, setConfigDrawerOpen] = useState(false);
  const [importConfigCSVOpen, setImportConfigCSVOpen] = useState(false);
  const [project, setProject] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [configsLoaded, setConfigsLoaded] = useState(false);
  const [configsLoading, setConfigsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [brochureUrl, setBrochureUrl] = useState(null);
  const [openDocumentsWithUploadBrochure, setOpenDocumentsWithUploadBrochure] = useState(false);
  const [detailsEditing, setDetailsEditing] = useState(false);
  const [detailsSaving, setDetailsSaving] = useState(false);
  const [tagsEditing, setTagsEditing] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [keyHighlightsEditing, setKeyHighlightsEditing] = useState(false);
  const [keyHighlightsSaving, setKeyHighlightsSaving] = useState(false);
  const [commissionEditing, setCommissionEditing] = useState(false);
  const [commissionSaving, setCommissionSaving] = useState(false);
  const [addressEditing, setAddressEditing] = useState(false);
  const [addressSaving, setAddressSaving] = useState(false);
  const [priceRateCardSaving, setPriceRateCardSaving] = useState(false);

  // Initial load: project detail only (no configs or media)
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      setConfigs([]);
      setConfigsLoaded(false);
      return;
    }
    setLoading(true);
    setConfigs([]);
    setConfigsLoaded(false);
    getProjectDetail(projectId)
      .then((proj) => setProject(proj ?? null))
      .catch(() => setProject(null))
      .finally(() => setLoading(false));
  }, [projectId, getProjectDetail]);

  // Fetch configurations when user clicks Configurations tab
  useEffect(() => {
    if (tab !== 'configuration' || !projectId || configsLoaded) return;
    setConfigsLoading(true);
    getConfigsByProjectId(projectId)
      .then((cfgs) => {
        setConfigs(Array.isArray(cfgs) ? cfgs : []);
        setConfigsLoaded(true);
      })
      .catch(() => setConfigs([]))
      .finally(() => setConfigsLoading(false));
  }, [tab, projectId, configsLoaded, getConfigsByProjectId]);

  // Fetch project media to know if brochure exists (for Upload/Download button)
  useEffect(() => {
    if (!projectId || !getMedia) return;
    getMedia('project', projectId)
      .then((data) => {
        const groups = (data && data.groups) || [];
        const brochureGroup = groups.find((g) => (g.asset_type || '').toLowerCase() === 'brochure');
        const firstAsset = brochureGroup && brochureGroup.assets && brochureGroup.assets[0];
        const url = firstAsset && (firstAsset.url || (firstAsset.urls && firstAsset.urls[0]));
        setBrochureUrl(url || null);
      })
      .catch(() => setBrochureUrl(null));
  }, [projectId, getMedia]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, p: 3 }}>
        <CircularProgress size={32} sx={{ color: 'success.main' }} />
      </Box>
    );
  }
  if (!project) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">Project not found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, width: '100%', alignSelf: 'flex-start' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/inventory/projects')}
        sx={{ mb: 2, textTransform: 'none', color: 'success.main' }}
      >
        Back to Projects
      </Button>

      <Box sx={{ mb: 2 }}>
        <ProjectDetailsHeader
          project={project}
          onEdit={() => setDetailsEditing(true)}
          onAddConfig={() => setConfigDrawerOpen(true)}
          onImportCSV={() => setImportConfigCSVOpen(true)}
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
          '& .Mui-selected': { color: 'success.main' },
          '& .MuiTabs-indicator': { bgcolor: 'success.main' },
        }}
      >
        <Tab label="Overview" value="overview" />
        <Tab label="Configurations" value="configuration" />
        <Tab label="Media & Documents" value="documents" />
      </Tabs>

      {tab === 'overview' && (
        <ProjectOverview
          project={project}
          onEditDetails={() => setDetailsEditing(true)}
          onSaveDetails={async (payload) => {
            if (!project?.id) return;
            setDetailsSaving(true);
            try {
              const updated = await updateProject(project.id, payload);
              setProject(updated);
              setDetailsEditing(false);
            } finally {
              setDetailsSaving(false);
            }
          }}
          isDetailsEditing={detailsEditing}
          detailsSaving={detailsSaving}
          onCancelDetails={() => setDetailsEditing(false)}
          onEditTags={() => setTagsEditing(true)}
          onSaveTags={async (tags) => {
            if (!project?.id) return;
            setTagsSaving(true);
            try {
              const updated = await updateProject(project.id, { tags: tags || [] });
              setProject(updated);
              setTagsEditing(false);
            } finally {
              setTagsSaving(false);
            }
          }}
          isTagsEditing={tagsEditing}
          tagsSaving={tagsSaving}
          onCancelTags={() => setTagsEditing(false)}
          onEditKeyHighlights={() => setKeyHighlightsEditing(true)}
          onSaveKeyHighlights={async (key_highlights) => {
            if (!project?.id) return;
            setKeyHighlightsSaving(true);
            try {
              const updated = await updateProject(project.id, { key_highlights: key_highlights || [] });
              setProject(updated);
              setKeyHighlightsEditing(false);
            } finally {
              setKeyHighlightsSaving(false);
            }
          }}
          onCancelKeyHighlights={() => setKeyHighlightsEditing(false)}
          isKeyHighlightsEditing={keyHighlightsEditing}
          keyHighlightsSaving={keyHighlightsSaving}
          constructionStatusOptions={inventoryOptions?.construction_statuses ?? []}
          areaUnitOptions={inventoryOptions?.area_units ?? []}
          onEditCommission={() => setCommissionEditing(true)}
          onSaveCommission={async (value) => {
            if (!project?.id) return;
            setCommissionSaving(true);
            try {
              const updated = await updateProject(project.id, { commission_rate: value });
              setProject(updated);
              setCommissionEditing(false);
            } finally {
              setCommissionSaving(false);
            }
          }}
          onCancelCommission={() => setCommissionEditing(false)}
          isCommissionEditing={commissionEditing}
          commissionSaving={commissionSaving}
          onAddTier={() => {}}
          onNavigateTab={setTab}
          onEditAddress={() => setAddressEditing(true)}
          onSaveAddress={async (payload) => {
            if (!project?.id) return;
            setAddressSaving(true);
            try {
              const updated = await updateProject(project.id, payload);
              setProject(updated);
              setAddressEditing(false);
            } finally {
              setAddressSaving(false);
            }
          }}
          onCancelAddress={() => setAddressEditing(false)}
          isAddressEditing={addressEditing}
          addressSaving={addressSaving}
          propertyTypes={inventoryOptions?.property_types ?? []}
          brochureDownloadUrl={brochureUrl}
          onUploadBrochure={() => { setTab('documents'); setOpenDocumentsWithUploadBrochure(true); }}
          onSavePriceRateCard={async ({ pricing }) => {
            if (!project?.id) return;
            setPriceRateCardSaving(true);
            try {
              const updated = await updateProject(project.id, { pricing });
              setProject(updated);
            } finally {
              setPriceRateCardSaving(false);
            }
          }}
          priceRateCardSaving={priceRateCardSaving}
        />
      )}
      {tab === 'configuration' && (
        configsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, p: 3 }}>
            <CircularProgress size={32} sx={{ color: 'success.main' }} />
          </Box>
        ) : (
          <ConfigurationList
            projectId={projectId}
            configs={configs}
            project={project}
            navigate={navigate}
            onConfigsRefetch={async () => {
              const cfgs = await getConfigsByProjectId(projectId);
              setConfigs(Array.isArray(cfgs) ? cfgs : []);
            }}
          />
        )
      )}
      {tab === 'documents' && (
        <MediaAndDocuments
          projectId={projectId}
          projectName={project?.name}
          type="documents"
          openUploadWithType={openDocumentsWithUploadBrochure ? 'brochure' : undefined}
          onOpenUploadWithTypeConsumed={() => setOpenDocumentsWithUploadBrochure(false)}
          onBrochureChange={() => {
            if (!projectId || !getMedia) return;
            getMedia('project', projectId).then((data) => {
              const groups = (data && data.groups) || [];
              const brochureGroup = groups.find((g) => (g.asset_type || '').toLowerCase() === 'brochure');
              const firstAsset = brochureGroup && brochureGroup.assets && brochureGroup.assets[0];
              const url = firstAsset && (firstAsset.url || (firstAsset.urls && firstAsset.urls[0]));
              setBrochureUrl(url || null);
            }).catch(() => setBrochureUrl(null));
          }}
        />
      )}

      <AddNewConfiguration
        open={configDrawerOpen}
        onClose={() => setConfigDrawerOpen(false)}
        projectId={projectId}
        projectName={project?.name}
        projectCommissionRate={project?.commissionDefault ?? project?.commission}
        projectPriceMin={project?.price_min != null ? Number(project.price_min) : undefined}
        projectPriceMax={project?.price_max != null ? Number(project.price_max) : undefined}
        categoryOptions={inventoryOptions?.categories ?? []}
        areaUnitOptions={inventoryOptions?.area_units ?? []}
        propertyTypeOptions={inventoryOptions?.property_types ?? []}
        onSubmit={async (payload) => {
          const category = typeof payload.category_id === 'number' ? payload.category_id : (payload.category_name || payload.category_id || 'residential');
          const propertyType = typeof payload.property_type_id === 'number' ? payload.property_type_id : (payload.property_type_name || payload.property_type_id);
          const apiPayload = {
            category,
            property_type: propertyType,
            name: payload.name || '',
            description: payload.description ?? '',
            price_min: payload.min_price ?? null,
            price_max: payload.max_price ?? null,
            area_min: payload.area_min ?? null,
            area_max: payload.area_max ?? null,
            area_unit: payload.area_unit || null,
            commission_rate: payload.commission_rate ?? null,
            tags: payload.tags || [],
            key_highlights: payload.key_highlights || [],
            ...(payload.metadata && Object.keys(payload.metadata).length > 0 ? { metadata: payload.metadata } : {}),
            ...(Array.isArray(payload.pricing) && payload.pricing.length > 0 ? { pricing: payload.pricing } : {}),
          };
          await createConfiguration(projectId, apiPayload);
          const cfgs = await getConfigsByProjectId(projectId);
          setConfigs(Array.isArray(cfgs) ? cfgs : []);
          setConfigDrawerOpen(false);
        }}
      />

      <CSVImportConfigurations
        open={importConfigCSVOpen}
        onClose={() => setImportConfigCSVOpen(false)}
        projectName={project?.name}
        onImport={async (payloads) => {
          for (const p of payloads) {
            await createConfiguration(projectId, {
              category: p.category || 'residential',
              property_type: p.property_type,
              name: p.name || '',
              description: '',
              price_min: p.min_price ?? null,
              price_max: p.max_price ?? null,
              area_min: null,
              area_max: null,
              area_unit: p.default_area_unit || 'sqft',
              commission_rate: p.commission_rate ?? null,
              tags: p.tags || [],
            });
          }
          const cfgs = await getConfigsByProjectId(projectId);
          setConfigs(Array.isArray(cfgs) ? cfgs : []);
          setImportConfigCSVOpen(false);
        }}
      />
    </Box>
  );
};

export { ProjectDetail };
